import React, { Fragment, useEffect, useState } from 'react'
import { StepperControls } from '../../../components/StepperTypes/StepperTypes'
import { PreviousStep } from './PreviousStep'
import { Box, Button, Grid, Typography } from '@mui/material'
import { AsnBlogCreate, AsnToReceive } from '../Interfaces'
import { useApi } from '../../../hooks/useApi'
import {
  getAsnToReceiveById,
  patchCancelReception,
  patchReceiveWithDifferences
} from '../api'
import { Status } from '../../../hooks/useFetchState'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal } from '../../../hooks/useModal'
import { useStyles } from '../styles'
import { Loading } from '../../../components/UI'
import { eAsnState } from '../../../enums/eAsnState'
import { stepEnum } from '../enum'

interface MoreOptionsProps {
  controls: StepperControls
  asnId: number
  openSnackBar: (msg: string | undefined, status: boolean) => void
}

export const MoreOptions: React.FC<MoreOptionsProps> = ({
  controls,
  asnId,
  openSnackBar
}) => {
  const { callApi, state } = useApi()
  const [selectedASN, setSelectedASN] = useState<AsnToReceive>()
  const { size, isOpen, title, openModal, closeModal } =
    useModal('Cancelar Recepción')
  const {
    size: receiveModalSize,
    isOpen: receiveModalIsOpen,
    title: receiveModalTitle,
    openModal: receiveModalOpenModal,
    closeModal: receiveModalCloseModal
  } = useModal('Recepcionar con Diferencias')

  const requestAsnToReceive = async () => {
    callApi(getAsnToReceiveById(asnId))
      .then((response: AsnToReceive) => {
        setSelectedASN(response)
      })
      .catch((error: any) => {
        openSnackBar(error, false)
      })
  }

  useEffect(() => {
    requestAsnToReceive()
  }, [])

  const requestCancelReception = () => {
    const body: AsnBlogCreate = {
      asnId: selectedASN?.asnId || 0,
      comments: 'Cancelar'
    }
    callApi(patchCancelReception(selectedASN?.asnId || 0, body))
      .then(() => {
        openSnackBar(
          `ASN: ${selectedASN?.asnNumber} - Recepción cancelada exitosamente`,
          true
        )
        controls.goToInitialStep()
      })
      .catch((error: any) => {
        openSnackBar(error, false)
      })
  }

  const requestReceiveWithDifferences = () => {
    const body: AsnBlogCreate = {
      asnId: selectedASN?.asnId || 0,
      comments: 'Diferencias'
    }
    callApi(patchReceiveWithDifferences(selectedASN?.asnId || 0, body))
      .then(() => {
        openSnackBar(
          `ASN: ${selectedASN?.asnNumber} - Recepción con diferencias exitosa`,
          true
        )
        controls.goToInitialStep()
      })
      .catch((error: any) => {
        openSnackBar(error, false)
      })
  }

  const isDisabledButton =
    state.status === Status.Loading ||
    selectedASN?.asnStateId === eAsnState.Pending

  const renderCancelModal = () => {
    const classes = useStyles()
    return (
      <Grid container direction='row' spacing={3}>
        <Grid item xs={12}>
          <Box padding={'20px'} textAlign='center'>
            <Typography variant='body1' gutterBottom>
              Cancelar recepción para la Asn: <b>{selectedASN?.asnNumber}</b>
            </Typography>
            <Typography variant='body1'>
              ¿Está seguro que desea cancelar el proceso de recepción de LPN?
              Esto llevará a dejar en estado pendiente todos los LPN
              registrados. Este proceso es{' '}
              <span className={classes.redText}>IRREVERSIBLE</span>
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Button
            onClick={closeModal}
            variant='outlined'
            color='primary'
            fullWidth
          >
            Cancelar
          </Button>
        </Grid>
        <Grid item xs={6}>
          {state.status === Status.Loading ? (
            <Loading />
          ) : (
            <Button
              onClick={() => {
                requestCancelReception()
              }}
              variant='contained'
              color='primary'
              fullWidth
            >
              Aceptar
            </Button>
          )}
        </Grid>
      </Grid>
    )

    return undefined
  }

  const renderModalReceiveAsnWithDifferences = () => {
    const classes = useStyles()
    return (
      <Grid container direction='row' spacing={3}>
        <Grid item xs={12}>
          <Box padding={'20px'} textAlign='center'>
            <Typography variant='body1' gutterBottom>
              Recepción con diferencias para la Asn:{' '}
              <b>{selectedASN?.asnNumber}</b>
            </Typography>
            <Typography variant='body1'>
              ¿Está seguro que desea recepcionar la ASN con menos LPN? Sólo
              quedarán en recepción las LPN controladas exitosamente, esta
              acción es <span className={classes.redText}>IRREVERSIBLE</span>
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Button
            onClick={receiveModalCloseModal}
            variant='outlined'
            color='primary'
            fullWidth
          >
            Cancelar
          </Button>
        </Grid>
        <Grid item xs={6}>
          {state.status === Status.Loading ? (
            <Loading />
          ) : (
            <Button
              onClick={() => {
                requestReceiveWithDifferences()
              }}
              variant='contained'
              color='primary'
              fullWidth
            >
              Aceptar
            </Button>
          )}
        </Grid>
      </Grid>
    )

    return undefined
  }

  return (
    <Fragment>
      <PreviousStep controls={controls} />
      <Typography
        variant='h5'
        style={{
          fontSize: '20px',
          fontWeight: 'bold',
          marginTop: '30px'
        }}
      >
        Solución de Problemas
      </Typography>
      <Typography style={{ fontWeight: 'bold' }}>
        ASN: {selectedASN?.asnNumber}
      </Typography>
      <Fragment>
        <Box
          component='div'
          display='flex'
          flexDirection='column'
          flexWrap='nowrap'
          alignItems='left'
          padding='10px 10px 0px 7px'
        >
          <Button
            fullWidth
            variant='contained'
            color='primary'
            style={{
              marginTop: '20px',
              width: '95%',
              minWidth: '95%'
            }}
            onClick={() => controls.goToStep(stepEnum.revertReceivedLpn, asnId)}
            disabled={isDisabledButton}
          >
            REVERTIR RECEPCIÓN DE LPN
          </Button>
          <Button
            fullWidth
            variant='contained'
            color='primary'
            style={{
              marginTop: '20px',
              width: '95%',
              minWidth: '95%'
            }}
            onClick={() => openModal('sm')}
            disabled={isDisabledButton}
          >
            CANCELAR RECEPCIÓN
          </Button>
          <Button
            fullWidth
            variant='contained'
            color='primary'
            style={{
              marginTop: '20px',
              width: '95%',
              minWidth: '95%'
            }}
            onClick={() => receiveModalOpenModal('sm')}
            disabled={isDisabledButton}
          >
            RECEPCIONAR ASN CON DIFERENCIAS
          </Button>
        </Box>
        <Modal open={isOpen} title={title} onClose={closeModal} maxWidth={size}>
          {renderCancelModal()}
        </Modal>
        <Modal
          open={receiveModalIsOpen}
          title={receiveModalTitle}
          onClose={receiveModalCloseModal}
          maxWidth={receiveModalSize}
        >
          {renderModalReceiveAsnWithDifferences()}
        </Modal>
      </Fragment>
    </Fragment>
  )
}
